import { useEffect, useRef, useState } from "react";
import { FileText, Paperclip, Send, X } from "lucide-react";

import { cn } from "@/lib/utils";

import { ContentBlock } from "../api/messages";
import { useRotatingText } from "../hooks/useRotatingText";

interface AttachedFile {
  id: string;
  file: File;
  preview?: string;
  contentBlock: ContentBlock;
}

interface EnhancedChatInputProps {
  onSend: (message: string, attachments?: ContentBlock[]) => void;
  disabled?: boolean;
  initialValue?: string;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
}

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 30 * 1024 * 1024;
const MAX_FILE_COUNT = 3;

const exampleQuestions = [
  "How should I price my first automation project?",
  "What's the best way to find warm leads in my network?",
  "How do I position myself as an AI consultant on LinkedIn?",
  "What should I include in a client proposal?",
  "How do I know if my niche is specific enough?",
];

// Shorter versions for mobile
const mobileExampleQuestions = [
  "How should I price my first project?",
  "How do I find warm leads?",
  "How do I position myself on LinkedIn?",
  "What to include in a proposal?",
  "Is my niche specific enough?",
];

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
  });
};

const isImageType = (mimeType: string) => mimeType.startsWith("image/");

export const EnhancedChatInput = ({
  onSend,
  disabled = false,
  initialValue = "",
  inputRef,
}: EnhancedChatInputProps) => {
  const [message, setMessage] = useState(initialValue);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isProcessingFiles, setIsProcessingFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef || internalTextareaRef;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentExample = useRotatingText(exampleQuestions, 4000);
  const currentMobileExample = useRotatingText(mobileExampleQuestions, 4000);

  // Update message when initialValue changes (from agent selection)
  useEffect(() => {
    if (initialValue) {
      setMessage(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [message, textareaRef]);

  useEffect(() => {
    return () => {
      attachedFiles.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
    };
  }, [attachedFiles]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (attachedFiles.length + files.length > MAX_FILE_COUNT) {
      alert(
        `You can only attach up to ${MAX_FILE_COUNT} files per message. Please remove some files first.`,
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setIsProcessingFiles(true);

    try {
      const newAttachments: AttachedFile[] = [];
      const skippedFiles: string[] = [];

      const currentTotalSize = attachedFiles.reduce(
        (sum, file) => sum + file.file.size,
        0,
      );

      for (const file of Array.from(files)) {
        if (!Object.keys(ACCEPTED_FILE_TYPES).includes(file.type)) {
          skippedFiles.push(`${file.name} (unsupported type)`);
          continue;
        }

        if (file.size > MAX_FILE_SIZE) {
          skippedFiles.push(
            `${file.name} (exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit)`,
          );
          continue;
        }

        const potentialTotalSize =
          currentTotalSize +
          newAttachments.reduce((sum, att) => sum + att.file.size, 0) +
          file.size;

        if (potentialTotalSize > MAX_TOTAL_SIZE) {
          skippedFiles.push(
            `${file.name} (would exceed ${MAX_TOTAL_SIZE / 1024 / 1024}MB total limit)`,
          );
          continue;
        }

        const base64Data = await fileToBase64(file);
        const isImage = isImageType(file.type);

        const contentBlock: ContentBlock = isImage
          ? {
              type: "image",
              data: base64Data,
              mimeType: file.type,
            }
          : {
              type: "file",
              data: base64Data,
              mimeType: file.type,
              filename: file.name,
            };

        newAttachments.push({
          id: `${Date.now()}-${file.name}`,
          file,
          preview: isImage ? URL.createObjectURL(file) : undefined,
          contentBlock,
        });
      }

      if (skippedFiles.length > 0) {
        alert(
          `The following files were skipped:\n\n${skippedFiles.join("\n")}`,
        );
      }

      setAttachedFiles((prev) => [...prev, ...newAttachments]);
    } finally {
      setIsProcessingFiles(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeAttachment = (id: string) => {
    setAttachedFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isProcessingFiles) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isProcessingFiles) return;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    const syntheticEvent = {
      target: { files },
    } as React.ChangeEvent<HTMLInputElement>;

    await handleFileSelect(syntheticEvent);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const hasContent = message.trim() || attachedFiles.length > 0;
    if (hasContent && !disabled && !isProcessingFiles) {
      const contentBlocks =
        attachedFiles.length > 0
          ? attachedFiles.map((f) => f.contentBlock)
          : undefined;
      onSend(message.trim(), contentBlocks);
      setMessage("");
      attachedFiles.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      setAttachedFiles([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const hasContent = message.trim() || attachedFiles.length > 0;
  const showPlaceholder = !isFocused && !message;

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div
        className={cn(
          "relative rounded-t-2xl border bg-white shadow-sm transition-all",
          isDragging
            ? "border-sds-purple-60 border-2 bg-sds-purple-05"
            : "border-gray-200 focus-within:border-sds-purple-60 focus-within:ring-2 focus-within:ring-sds-purple-60/20",
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-sds-purple-05 border-2 border-dashed border-sds-purple-60 rounded-2xl flex items-center justify-center z-10 pointer-events-none">
            <div className="text-center px-4">
              <p className="text-sm font-medium text-sds-purple-80">
                Drop files here
              </p>
              <p className="text-xs text-sds-neutral-60 mt-1">
                PDF, PNG, JPG, GIF, WebP (max {MAX_FILE_COUNT} files,{" "}
                {MAX_TOTAL_SIZE / 1024 / 1024}MB total)
              </p>
            </div>
          </div>
        )}

        {/* Placeholder overlay */}
        {showPlaceholder && (
          <div className="absolute inset-0 flex flex-col justify-center pl-12 pr-14 md:pl-14 md:pr-16 py-4 pointer-events-none">
            {/* Mobile: shorter text */}
            <span className="md:hidden text-gray-500 text-sm font-semibold leading-tight">
              Ask any question
            </span>
            <span className="md:hidden text-gray-400 text-xs leading-tight mt-0.5">
              {currentMobileExample}
            </span>
            {/* Desktop: full text */}
            <span className="hidden md:block text-gray-500 text-base font-semibold leading-tight">
              Ask any question or try one of our agents
            </span>
            <span className="hidden md:block text-gray-400 text-sm leading-tight mt-0.5">
              e.g., "{currentExample}"
            </span>
          </div>
        )}

        {/* Attachments preview */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 px-4 pt-4">
            {attachedFiles.map((attachment) => (
              <div
                key={attachment.id}
                className="relative group flex items-center gap-2 bg-sds-neutral-05 rounded-md px-2 py-1.5 border border-sds-neutral-20"
              >
                {attachment.preview ? (
                  <img
                    src={attachment.preview}
                    alt={attachment.file.name}
                    className="w-8 h-8 object-cover rounded"
                  />
                ) : (
                  <FileText className="w-5 h-5 text-sds-neutral-60" />
                )}
                <span className="text-xs text-sds-neutral-80 max-w-[120px] truncate">
                  {attachment.file.name}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(attachment.id)}
                  className="p-0.5 rounded-full hover:bg-sds-neutral-20 transition-colors"
                >
                  <X className="w-3.5 h-3.5 text-sds-neutral-60" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="flex items-end px-3 py-4 md:px-4 md:py-5 min-h-[72px] md:min-h-[80px]">
          {/* Attachment button */}
          <button
            type="button"
            onClick={openFilePicker}
            disabled={disabled || isProcessingFiles}
            className={cn(
              "p-1.5 rounded-md transition-colors flex-shrink-0 mr-2",
              disabled || isProcessingFiles
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-sds-neutral-10 text-sds-neutral-60 hover:text-sds-neutral-80",
            )}
            title="Attach files (PDF, images)"
          >
            <Paperclip className="w-5 h-5" />
          </button>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={Object.entries(ACCEPTED_FILE_TYPES)
              .flatMap(([mime, exts]) => [mime, ...exts])
              .join(",")}
            onChange={handleFileSelect}
            className="hidden"
          />

          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            rows={1}
            className="flex-1 bg-transparent outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 text-sds-neutral-80 text-base font-['Plus_Jakarta_Sans'] tracking-normal resize-none overflow-hidden py-1"
            style={{ boxShadow: "none", maxHeight: "200px" }}
          />
          <button
            type="submit"
            disabled={disabled || !hasContent || isProcessingFiles}
            className={cn(
              "rounded-full p-2 flex items-center justify-center transition-colors flex-shrink-0 ml-2",
              disabled || !hasContent || isProcessingFiles
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-sds-purple-100 text-white hover:bg-sds-purple-80",
            )}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </form>
  );
};
